/** Sessões partilhadas: os pedidos à API (/api/sessoes). A lista de
 *  acessos gere-se pelos mesmos pedidos das outras partilhas
 *  (core/partilhas/partilhasApi.ts). */
import { api } from '@core/api/client'
import { pedidoDeBloco } from '@core/modelos/modelosApi'
import type { PartilhaInfo } from '@core/partilhas/partilhasRegras'
import type { SessaoNaReciclagem } from '@core/modelos/reciclagemRegras'
import type { ManifestoDaSessao, PecaDaSessao } from './sessoesRegras'

const BASE = '/api/sessoes'

export interface SessaoDoServidor {
  partilha: PartilhaInfo
  /** Lido com `lerManifesto` antes de se usar. */
  manifesto: unknown
  versao: number
  actualizado_em: string | null
  pecas: PecaDaSessao[]
  avisos: string[]
}

export interface PartilharSessaoIn {
  chave: string
  nome: string
  manifesto: ManifestoDaSessao
  pecas: string[]
  workspace_inteiro: boolean
}

/** Uma sessão partilhada deste workspace, como a lista a mostra. */
export interface SessaoResumo {
  id: string
  nome: string | null
  dono_nome: string | null
  pode_gerir: boolean
  actualizado_em: string | null
  /** O espaço dos pontos de vista (só para quem a partilhou). */
  bytes: number
  /** Peças de outras pessoas à espera da autorização delas. */
  por_autorizar: number
}

/** Um pedido para incluir uma peça minha na sessão de outra pessoa. */
export interface PedidoDeSessao {
  sessao_id: string
  sessao_nome: string | null
  pedido_por: string | null
  peca_id: string
  peca_tipo: 'modelo' | 'bcf' | 'triagem'
  peca_nome: string | null
  pedida_em: string | null
  /** Quem passa a ter acesso à peça se eu autorizar. */
  pessoas: string[]
}

export const sessoesApi = {
  /** As sessões partilhadas deste workspace que a pessoa vê (as suas,
   *  as partilhadas com ela e as do workspace inteiro). */
  listar: () => api.get<SessaoResumo[]>(BASE),
  /** Partilha a sessão, ou actualiza-a se a chave já for de uma. */
  partilhar: (e: PartilharSessaoIn) => api.post<SessaoDoServidor>(BASE, e),
  ver: (id: string) => api.get<SessaoDoServidor>(`${BASE}/${encodeURIComponent(id)}`),
  actualizar: (
    id: string,
    e: { nome?: string; manifesto?: ManifestoDaSessao; pecas?: string[] },
  ) => api.put<SessaoDoServidor>(`${BASE}/${encodeURIComponent(id)}`, e),
  /** Apaga a sessão (só quem a partilhou): tira logo os acessos que ela
   *  deu e vai para a Reciclagem, onde se restaura durante 7 dias. */
  apagar: (id: string) => api.delete<void>(`${BASE}/${encodeURIComponent(id)}`),
  /** As sessões deste workspace na Reciclagem que a pessoa pode restaurar. */
  reciclagem: () => api.get<SessaoNaReciclagem[]>(`${BASE}/reciclagem`),
  restaurar: (id: string) => api.post<void>(`${BASE}/${encodeURIComponent(id)}/restaurar`),
  /** As peças minhas que outras pessoas pediram para as sessões delas. */
  pedidos: () => api.get<PedidoDeSessao[]>(`${BASE}/pedidos/por-autorizar`),
  autorizar: (sessao: string, peca: string) =>
    api.post<void>(`${BASE}/${encodeURIComponent(sessao)}/pecas/${encodeURIComponent(peca)}/autorizar`),
  recusar: (sessao: string, peca: string) =>
    api.post<void>(`${BASE}/${encodeURIComponent(sessao)}/pecas/${encodeURIComponent(peca)}/recusar`),
  /** Um bloco do pacote dos pontos de vista (só quem partilhou). */
  enviarBloco: async (id: string, indice: number, dados: Blob): Promise<void> => {
    await pedidoDeBloco(`${BASE}/${encodeURIComponent(id)}/blocos/${indice}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: dados,
    })
  },
  lerBloco: async (id: string, indice: number): Promise<ArrayBuffer> => {
    const res = await pedidoDeBloco(`${BASE}/${encodeURIComponent(id)}/blocos/${indice}`, { method: 'GET' })
    return await res.arrayBuffer()
  },
}
